import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import classes from './createCourse.module.css';
import Header from '../../components/standartComponents/header';
import { getCSRFToken, fetchCSRFToken } from '../../lib/csrf';
import { useTranslation } from 'react-i18next';

const LEVEL_BADGE = { 1: 'A1', 2: 'A0', 3: 'A2', 4: 'B1', 5: 'B2', 6: 'C1', 7: 'pre-A1' };

const getLevelBadge = (levels) => {
    if (!levels?.length) return null;
    if (levels.length > 1) {
        return `${LEVEL_BADGE[levels[0]]}–${LEVEL_BADGE[levels[levels.length - 1]]}`;
    }
    return LEVEL_BADGE[levels[0]] || null;
};

const TOPICS = [
    { id: 'work',     nameKey: 'Work',         descKey: 'Job interviews, meetings, correspondence, and workplace dialogues.', icon: '💼', bg: '#EEF0FF', purposeId: 2 },
    { id: 'travel',   nameKey: 'Travel',       descKey: 'Airport, hotel, café, and meeting people abroad.',                   icon: '✈️', bg: '#FEF3C7', purposeId: 1 },
    { id: 'study',    nameKey: 'Study',        descKey: 'Educational topics, explanations, and academic vocabulary.',         icon: '🎓', bg: '#E0E3FF', purposeId: 3 },
    { id: 'social',   nameKey: 'Socializing',  descKey: 'Small talk, meeting people, and everyday conversations.',            icon: '💬', bg: '#ECFDF5', purposeId: 4 },
    { id: 'personal', nameKey: 'For yourself', descKey: 'Everyday topics, hobbies, and light practice.',                      icon: '🌿', bg: '#FFF1F2', purposeId: 5 },
    { id: 'tech',     nameKey: 'News & Tech',  descKey: 'Current topics, technology, news, and discussions.',                 icon: '📱', bg: '#F0F9FF', purposeId: 5 },
    { id: 'health',   nameKey: 'Health',       descKey: 'Doctor visits, wellbeing, pharmacy, and basic situations.',          icon: '🩺', bg: '#ECFDF5', purposeId: 5 },
    { id: 'leisure',  nameKey: 'Leisure',      descKey: 'Plans, weekends, leisure, and meetings.',                            icon: '🎯', bg: '#FFFBEB', purposeId: 5 },
];

const CreateCourse = ({ authUser }) => {
    const navigate = useNavigate();
    const { i18n, t } = useTranslation();
    
    const [selTopic, setSelTopic]     = useState(null);
    const [selLevel, setSelLevel]     = useState(null);
    const [isLoading, setIsLoading]   = useState(false);
    const [error, setError]           = useState(null);
    const [levels, setLevels]         = useState([]);
    const [levelsLoading, setLevelsLoading] = useState(true);
    const [levelsError, setLevelsError]   = useState(null);

    useEffect(() => {
        const fetchLevels = async () => {
            setLevelsLoading(true);
            setLevelsError(null);
            try {
                const res = await fetch('/api/v1/language-level-questionnaire', {
                    credentials: 'include',
                    headers: { 'Accept-Language': i18n.language || 'uk' },
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();
                const sorted = [...data.results];
                const preA1Idx = sorted.findIndex(l => (l.levels || []).includes(7));
                if (preA1Idx > 0) {
                    const [preA1] = sorted.splice(preA1Idx, 1);
                    sorted.unshift(preA1);
                }
                setLevels(sorted);
            } catch (err) {
                console.error('Error fetching levels:', err);
                setLevelsError(t("Failed to load levels. Please try again."));
            } finally {
                setLevelsLoading(false);
            }
        };
        fetchLevels();
    }, [i18n.language, t]);

    useEffect(() => {
        if (selLevel && levels.length > 0) {
            const stillExists = levels.some(l => l.id === selLevel);
            if (!stillExists) setSelLevel(null);
        }
    }, [levels, selLevel]);

    const topicObj = TOPICS.find(t => t.id === selTopic);
    const levelObj = levels.find(l => l.id === selLevel);

    const handleCreate = async () => {
        if (!selTopic || !selLevel || isLoading) return;
        setIsLoading(true);
        setError(null);
        try {
            let csrfToken = getCSRFToken();
            if (!csrfToken) { await fetchCSRFToken(); csrfToken = getCSRFToken(); }
            const headers = { 'Content-Type': 'application/json', ...(csrfToken && { 'X-CSRFToken': csrfToken }) };

            const profileRes = await fetch('/api/v1/user-profile/current', { method: 'GET', credentials: 'include', headers });
            if (!profileRes.ok) throw new Error(`Failed to fetch profile: HTTP ${profileRes.status}`);
            const profile = await profileRes.json();

            const existingLevels = profile.english_levels || [];
            const existingPurposes = profile.english_purposes || [];
            const newLevelInts = levelObj?.levels || [];
            const newPurposeId = topicObj.purposeId;

            const mergedLevels = [...existingLevels, ...newLevelInts.filter(lvl => !existingLevels.includes(lvl))];
            const mergedPurposes = existingPurposes.includes(newPurposeId) ? existingPurposes : [...existingPurposes, newPurposeId];

            const levelsChanged = newLevelInts.some(lvl => !existingLevels.includes(lvl));
            const purposesChanged = !existingPurposes.includes(newPurposeId);

            if (levelsChanged || purposesChanged) {
                const patchRes = await fetch('/api/v1/user-profile/current', {
                    method: 'PATCH', credentials: 'include', headers,
                    body: JSON.stringify({ english_levels: mergedLevels, english_purposes: mergedPurposes }),
                });
                if (!patchRes.ok) {
                    const errData = await patchRes.json().catch(() => ({}));
                    throw new Error(errData?.detail || `HTTP ${patchRes.status}`);
                }
            }
            navigate('/roadMap');
        } catch (err) {
            console.error('Error creating course:', err);
            setError(t("Failed to create course. Please try again."));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <Header authUser={authUser} />
            <div className={classes.page}>
                <div id="mainFlow">
                    <div className={classes.pgT}>{t("What would you like to learn next?")} 🎯</div>
                    <div className={classes.pgS}>{t("Choose a topic and level for the new course. Completed courses will remain in your account.")}</div>
                    
                    {/* TOPICS */}
                    <div className={classes.sec}>
                        <div className={classes.secT}>{t("Which topic would you like to study?")}</div>
                        <div className={classes.secS}>{t("We will select lessons based on your new learning area.")}</div>
                        <div className={classes.tgrid}>
                            {TOPICS.map(topic => (
                                <div key={topic.id} className={`${classes.tc} ${selTopic === topic.id ? classes.tcSel : ''}`} onClick={() => setSelTopic(topic.id)}>
                                    <div className={classes.tcCheck}>
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
                                    </div>
                                    <div className={classes.tcIco} style={{ background: topic.bg }}>{topic.icon}</div>
                                    <div className={classes.tcName}>{t(topic.nameKey)}</div>
                                    <div className={classes.tcDesc}>{t(topic.descKey)}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* LEVELS */}
                    <div className={classes.sec}>
                        <div className={classes.secT}>{t("Which level should this course start from?")}</div>
                        <div className={classes.secS}>{t("Choose a level — you can change it later in the course settings.")}</div>
                        {levelsLoading ? (
                            <div className={classes.lvLoading}>{t("Loading levels...")}</div>
                        ) : levelsError ? (
                            <div className={classes.lvErr}>{levelsError}</div>
                        ) : (
                            <div className={classes.lvls}>
                                {levels.map(l => (
                                    <div key={l.id} className={`${classes.lv} ${selLevel === l.id ? classes.lvSel : ''}`} onClick={() => setSelLevel(l.id)}>
                                        {getLevelBadge(l.levels) && <div className={classes.lvBadge}>{getLevelBadge(l.levels)}</div>}
                                        <div className={classes.lvInfo}><span className={classes.lvName}>{l.option}</span></div>
                                        <div className={classes.lvRadio}></div>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className={classes.lvHelp}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
                            {t("Not sure? Choose an approximate level — you can change it later in the course settings.")}
                        </div>
                    </div>

                    {/* SUMMARY */}
                    <div className={`${classes.sum} ${selTopic && selLevel ? classes.sumSh : ''}`}>
                        <div className={classes.sumT}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
                            {t("New course")}
                        </div>
                        <div className={classes.sumGrid}>
                            <div className={classes.sumItem}><div className={classes.sumLabel}>{t("Topic")}</div><div className={classes.sumVal}>{topicObj ? t(topicObj.nameKey) : '—'}</div></div>
                            <div className={classes.sumItem}><div className={classes.sumLabel}>{t("Level")}</div><div className={classes.sumVal}>{levelObj?.option || '—'}</div></div>
                            <div className={classes.sumItem}><div className={classes.sumLabel}>{t("Approximately")}</div><div className={classes.sumVal}>{t("18 lessons")}</div></div>
                        </div>
                        <div className={classes.sumNote}>{t("The course will be added to “My courses”. Completed courses will remain available.")}</div>
                    </div>

                    {error && <div className={classes.errorMsg}>{error}</div>}

                    {/* ACTIONS */}
                    <div className={classes.acts}>
                        <button className={`${classes.btn} ${classes.btnS}`} onClick={() => window.history.back()} disabled={isLoading}>
                            {t("Cancel")}
                        </button>
                        <button className={`${classes.btn} ${classes.btnP} ${(!selTopic || !selLevel || isLoading) ? classes.dis : ''}`} onClick={handleCreate} disabled={!selTopic || !selLevel || isLoading}>
                            {isLoading ? t("Saving...") : t("Create course")}
                            {!isLoading && <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default CreateCourse;